/* eslint-disable import/max-dependencies -- create-case wizard orchestrates patient, visit, and case-details creation across several hooks */
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { addHours } from "date-fns";
import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { useCreateCase } from "../api/useCreateCase";
import { useCreatePatient } from "../api/useCreatePatient";
import { useCreateVisit } from "../api/useCreateVisit";
import {
  useFormatTimestampForWorkplace,
  useFormatWorkplaceDateInput,
} from "../api/useFormatTimestampForWorkplace";
import { useCanSetRates } from "../../lib/useCanSetRates";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import { useRateConfiguration } from "../../lib/useRateConfiguration";
import { isCaseDescriptionValid } from "../caseDescriptionValidation";
import { HOME_HEALTH_CASES_PATH } from "../paths";
import { isHourlyPricingAllowed, PricingType, VisitType, WorkerRequirements } from "../types";
import { buildCreateVisitAttributes } from "./careTeam/buildCreateVisitAttributes";
import {
  createVisitDraft,
  DEFAULT_VISIT_DRAFT_FIELDS,
  type VisitDraft,
  type VisitDraftFields,
} from "./careTeam/visitDraft";
import { type SelectedAddress } from "./MobileAddressAutocompleteField";
import { type CareTeamWizardScreen, MobileCareTeamWizardView } from "./MobileCareTeamWizardView";
import { getMobileCaseStep2FieldErrors } from "./mobileCaseStep2Validation";

interface VisitEditorState {
  id: string | null;
  step: "discipline" | "schedule";
  fields: VisitDraftFields;
}

// Local mirrors of the View's Step1Data / CaseDetailsData props (structurally
// compatible), so the container doesn't import them just to annotate state.
interface PatientData {
  patientName: string;
  address: string;
  oasis: boolean;
}

interface CaseDetails {
  specialties: string[];
  description: string;
  modificationReason: string;
}

const DEFAULT_PATIENT: PatientData = { patientName: "", address: "", oasis: false };
const DEFAULT_CASE_DETAILS: CaseDetails = {
  specialties: [],
  description: "",
  modificationReason: "",
};

function pluralizeVisits(count: number): string {
  return `${count} ${count === 1 ? "visit" : "visits"}`;
}

export function MobileCreateCaseWizardContainer() {
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  const [phase, setPhase] = useState<"patient" | "careTeam" | "details">("patient");
  const [patient, setPatient] = useState<PatientData>(DEFAULT_PATIENT);
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress | undefined>(undefined);
  const [drafts, setDrafts] = useState<VisitDraft[]>([]);
  const [editor, setEditor] = useState<VisitEditorState | null>(null);
  const [caseDetails, setCaseDetails] = useState<CaseDetails>(DEFAULT_CASE_DETAILS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutateAsync: createPatient, isLoading: isCreatingPatient } = useCreatePatient();
  const { mutateAsync: createCase, isLoading: isCreatingCase } = useCreateCase();
  const { mutateAsync: createVisit, isLoading: isCreatingVisit } = useCreateVisit();
  const isSubmitting = isCreatingPatient || isCreatingCase || isCreatingVisit;

  // Idempotency guards so a retry doesn't re-create the patient/case or re-post
  // visits that already succeeded.
  const createdRef = useRef<{ patientId?: string; caseId?: string }>({});
  const createdDraftIds = useRef<Set<string>>(new Set());

  const canSetRates = useCanSetRates();
  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    caseDetails.modificationReason
  );
  const formatTimestampForWorkplace = useFormatTimestampForWorkplace();
  const formatWorkplaceDateInput = useFormatWorkplaceDateInput();
  const todayIso = formatWorkplaceDateInput(new Date().toISOString());

  // Rate config for the visit being edited; the address is locked once a visit
  // exists so it stays consistent across drafts.
  const rates = useRateConfiguration({
    visitType: (editor?.fields.visitType as VisitType) || undefined,
    workerRequirements: (editor?.fields.workerRequirement as WorkerRequirements) || undefined,
    pricingType: editor?.fields.pricingType ?? PricingType.PER_VISIT,
    patientAddress: patient.address,
  });
  const editorFields = editor?.fields ?? DEFAULT_VISIT_DRAFT_FIELDS;
  const scheduleErrors = getMobileCaseStep2FieldErrors({
    data: editorFields,
    rates,
    pricingType: editorFields.pricingType,
    todayIso,
  });

  // A valid address requires a picked suggestion (for coordinates); editing the
  // text afterward invalidates it until another suggestion is selected.
  const hasValidAddress =
    patient.address.length > 0 && selectedAddress?.formattedAddress === patient.address;
  const isPatientValid = patient.patientName.trim().length > 0 && hasValidAddress;

  function handlePatientChange(field: keyof PatientData, value: string | boolean) {
    setPatient((prev) => ({ ...prev, [field]: value }));
  }

  function handleAddressSelect(address: SelectedAddress) {
    setSelectedAddress(address);
    setPatient((prev) => ({ ...prev, address: address.formattedAddress }));
  }

  function handleEditorFieldChange(field: keyof VisitDraftFields, value: string | PricingType) {
    setEditor((prev) => {
      if (!prev) {
        return prev;
      }

      const fields: VisitDraftFields = { ...prev.fields, [field]: value };
      if (
        (field === "visitType" || field === "workerRequirement") &&
        !isHourlyPricingAllowed({
          visitType: fields.visitType as VisitType,
          workerReq: fields.workerRequirement as WorkerRequirements,
        })
      ) {
        fields.pricingType = PricingType.PER_VISIT;
      }

      return { ...prev, fields };
    });
  }

  function parseDateFieldToISO(dateString: string): string {
    const [yearStr, monthStr, dayStr] = dateString.split("-");
    const localDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
    return formatTimestampForWorkplace(addHours(localDate, 12));
  }

  function isDisciplineValid(fields: VisitDraftFields): boolean {
    return Boolean(fields.visitType) && Boolean(fields.workerRequirement);
  }

  function isScheduleValid(fields: VisitDraftFields): boolean {
    const isRegularVisit = fields.visitType === VisitType.REGULAR;
    const hasSchedule = isRegularVisit
      ? Boolean(fields.visitsPerWeek) && Boolean(fields.totalWeeks)
      : true;
    const hasRates = canSetRates
      ? Number.isFinite(Number.parseFloat(fields.chargePerVisit)) &&
        Number.isFinite(Number.parseFloat(fields.payPerVisit))
      : true;
    const hasEstimatedDuration =
      fields.pricingType === PricingType.PER_HOUR
        ? Number.parseFloat(fields.estimatedDuration) >= 2
        : true;
    return (
      Boolean(fields.firstVisitDueDate) &&
      hasSchedule &&
      hasRates &&
      hasEstimatedDuration &&
      Object.keys(scheduleErrors).length === 0
    );
  }

  function openNewVisitEditor() {
    setEditor({ id: null, step: "discipline", fields: DEFAULT_VISIT_DRAFT_FIELDS });
  }

  function goToCareTeam() {
    setPhase("careTeam");
    // Jump straight into configuring the first visit rather than an empty list.
    if (drafts.length === 0) {
      openNewVisitEditor();
    }
  }

  function openEditVisitEditor(draftId: string) {
    const draft = drafts.find((item) => item.id === draftId);
    if (!draft) {
      return;
    }

    const { id: _id, ...fields } = draft;
    setEditor({ id: draftId, step: "discipline", fields });
  }

  function commitEditor() {
    if (!editor) {
      return;
    }

    const { id, fields } = editor;
    setDrafts((prev) =>
      id === null
        ? [...prev, createVisitDraft(fields)]
        : prev.map((draft) => (draft.id === id ? { ...draft, ...fields } : draft))
    );
    setEditor(null);
  }

  function removeDraft(draftId: string) {
    setDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
  }

  function handleCaseDetailsChange(field: keyof CaseDetails, value: string | string[]) {
    setCaseDetails((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    setSubmitError(null);
    const { specialties, description, modificationReason } = caseDetails;

    let patientId: string;
    let caseId: string;
    try {
      patientId =
        createdRef.current.patientId ??
        (
          await createPatient({
            data: {
              attributes: {
                workplaceId,
                externalPatientIdentifier: patient.patientName,
                formattedAddress: patient.address,
                latitude: selectedAddress?.latitude ?? 0,
                longitude: selectedAddress?.longitude ?? 0,
                oasis: patient.oasis,
                modificationReason,
              },
              type: "patient",
            },
          })
        ).id;
      createdRef.current.patientId = patientId;

      caseId =
        createdRef.current.caseId ??
        (
          await createCase({
            data: {
              attributes: { patientId, workplaceId, specialties, description, modificationReason },
              type: "case",
            },
          })
        ).id;
      createdRef.current.caseId = caseId;
    } catch {
      setSubmitError("Something went wrong. Please try again.");
      return;
    }

    const failedDraftIds: string[] = [];
    for (const draft of drafts) {
      if (createdDraftIds.current.has(draft.id)) {
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop -- sequential so the created-set stays consistent on retry
        const visit = await createVisit({
          data: {
            attributes: buildCreateVisitAttributes({
              draft,
              caseId,
              modificationReason,
              canSetRates,
              toDeadlineIso: parseDateFieldToISO,
            }),
            type: "visit",
          },
        });
        createdDraftIds.current.add(draft.id);
        logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_CREATED_WITH_CASE, {
          workplaceId,
          workerReq: draft.workerRequirement || undefined,
          type: draft.visitType,
          visitId: visit.id,
          caseId,
          patientId,
          performedByAdmin: isAdmin,
        });
      } catch {
        failedDraftIds.push(draft.id);
      }
    }

    if (failedDraftIds.length === 0) {
      setIsSuccess(true);
    } else {
      setSubmitError(
        `The case was created, but ${pluralizeVisits(failedDraftIds.length)} couldn't be posted. Please try again.`
      );
    }
  }

  const screen: CareTeamWizardScreen = isSuccess
    ? "success"
    : phase === "patient"
      ? "patient"
      : phase === "details"
        ? "details"
        : editor !== null
          ? editor.step
          : "review";

  function handleBack() {
    if (phase === "patient") {
      history.goBack();
    } else if (phase === "details") {
      setPhase("careTeam");
    } else if (editor !== null && editor.step === "schedule") {
      setEditor((prev) => (prev ? { ...prev, step: "discipline" } : prev));
    } else if (editor !== null && drafts.length > 0) {
      setEditor(null);
    } else if (editor !== null) {
      // First visit (no drafts yet) — back to patient details.
      setEditor(null);
      setPhase("patient");
    } else {
      setPhase("patient");
    }
  }

  function handlePrimary() {
    if (phase === "patient") {
      goToCareTeam();
    } else if (phase === "details") {
      void handleSubmit();
    } else if (editor !== null && editor.step === "discipline") {
      setEditor((prev) => (prev ? { ...prev, step: "schedule" } : prev));
    } else if (editor !== null) {
      commitEditor();
    } else {
      setPhase("details");
    }
  }

  const primaryLabel =
    phase === "patient"
      ? "Next"
      : phase === "details"
        ? "Post case"
        : editor !== null && editor.step === "discipline"
          ? "Next"
          : editor !== null
            ? editor.id === null
              ? "Add visit"
              : "Save visit"
            : "Continue";

  const primaryDisabled =
    isSubmitting ||
    (phase === "patient"
      ? !isPatientValid
      : phase === "details"
        ? !isModificationReasonSatisfied || !isCaseDescriptionValid(caseDetails.description)
        : editor !== null && editor.step === "discipline"
          ? !isDisciplineValid(editor.fields)
          : editor !== null
            ? !isScheduleValid(editor.fields)
            : drafts.length === 0);

  return (
    <MobileCareTeamWizardView
      title="Post a new case"
      progressSteps={["patient", "visits", "details"]}
      activeStepIndex={phase === "patient" ? 0 : phase === "careTeam" ? 1 : 2}
      screen={screen}
      submitError={submitError}
      patient={{
        data: patient,
        addressLocked: drafts.length > 0,
        onChange: handlePatientChange,
        onAddressSelect: handleAddressSelect,
      }}
      editorFields={editorFields}
      rates={rates}
      scheduleErrors={scheduleErrors}
      onEditorFieldChange={handleEditorFieldChange}
      caseDetails={caseDetails}
      onCaseDetailsChange={handleCaseDetailsChange}
      drafts={drafts}
      onAddAnother={openNewVisitEditor}
      onEditDraft={openEditVisitEditor}
      onRemoveDraft={removeDraft}
      successVisitCount={drafts.length}
      successVariant="case"
      onDone={() => {
        history.replace(HOME_HEALTH_CASES_PATH);
      }}
      back={{ label: "Back", disabled: isSubmitting, onClick: handleBack }}
      primary={{ label: primaryLabel, disabled: primaryDisabled, onClick: handlePrimary }}
    />
  );
}
